import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Check, Send, FileDown, Loader2, FolderOpen, Mail, Eye, History, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CatalogueTraiteurPage() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited
  const [creating, setCreating] = useState(false);
  const [activeCat, setActiveCat] = useState('all');
  const [sendModal, setSendModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/catalogue-traiteur/items`).then(r => r.json()),
        fetch(`${API}/api/catalogue-traiteur/categories`).then(r => r.json()),
      ]);
      setItems(Array.isArray(r1) ? r1 : []);
      setCategories(Array.isArray(r2) ? r2 : []);
    } catch {
      toast.error('Erreur chargement catalogue');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = activeCat === 'all' ? items : items.filter(i => i.categorie === activeCat);

  const handleSave = async (item) => {
    try {
      const isNew = !item.ref || creating;
      const url = isNew
        ? `${API}/api/catalogue-traiteur/items`
        : `${API}/api/catalogue-traiteur/items/${item.ref}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item),
      });
      if (r.ok) {
        toast.success(isNew ? 'Item créé' : 'Item modifié');
        setEditing(null);
        setCreating(false);
        load();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (ref, nom) => {
    if (!window.confirm(`Supprimer "${nom}" ?`)) return;
    try {
      await fetch(`${API}/api/catalogue-traiteur/items/${ref}`, { method: 'DELETE' });
      toast.success('Supprimé');
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-4" data-testid="catalogue-traiteur">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
            🥂 Catalogue Traiteur
          </h2>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{items.length} prestations · {categories.length} catégories</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setHistoryModal(true)}
            className="flex items-center gap-2 px-3 h-9 rounded-lg bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] text-sm font-bold hover:bg-[rgb(var(--lm-bg))] transition-all"
            data-testid="history-catalogue-btn">
            <History size={14} /> Envois & Ouvertures
          </button>
          <button
            onClick={() => setSendModal(true)}
            className="flex items-center gap-2 px-3 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-all"
            data-testid="send-catalogue-btn">
            <Send size={14} /> Envoyer à un client
          </button>
          <button
            onClick={() => { setCreating(true); setEditing({ nom: '', categorie: categories[0]?.nom || '', desc: '', prix_ttc: 0, unite: '€' }); }}
            className="flex items-center gap-2 px-3 h-9 rounded-lg bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-sm font-bold hover:opacity-90"
            data-testid="add-item-btn">
            <Plus size={14} /> Nouvelle prestation
          </button>
        </div>
      </div>

      {/* Filtres catégorie */}
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => setActiveCat('all')}
          className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${activeCat === 'all' ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))]' : 'bg-[rgb(var(--lm-card))] text-[rgb(var(--lm-text-muted))]'}`}
          data-testid="filter-all">
          Tout ({items.length})
        </button>
        {categories.map(c => (
          <button key={c.nom} onClick={() => setActiveCat(c.nom)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${activeCat === c.nom ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))]' : 'bg-[rgb(var(--lm-card))] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
            data-testid={`filter-${c.nom}`}>
            {c.nom} ({c.nb_items})
          </button>
        ))}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : (
        <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20">
          {filtered.map(item => (
            <div key={item.ref} className="px-3 py-2.5 hover:bg-[rgb(var(--lm-bg))]/50 group">
              {editing?.ref === item.ref ? (
                <ItemForm item={editing} onChange={setEditing} onSave={() => handleSave(editing)} onCancel={() => setEditing(null)} categories={categories} />
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">
                        {item.categorie}
                      </span>
                      <p className="text-sm font-bold text-[rgb(var(--lm-text))] truncate">{item.nom}</p>
                    </div>
                    {item.desc && <p className="text-[11px] text-[rgb(var(--lm-text-muted))] mt-0.5 truncate">{item.desc}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-sm font-bold text-[rgb(var(--lm-gold))]">{item.prix_ttc?.toFixed(2)} {item.unite}</span>
                    <button onClick={() => setEditing({ ...item })} className="p-1.5 rounded hover:bg-blue-500/15 text-[rgb(var(--lm-text-muted))] hover:text-blue-400 opacity-0 group-hover:opacity-100 transition-all" data-testid={`edit-${item.ref}`}>
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => handleDelete(item.ref, item.nom)} className="p-1.5 rounded hover:bg-red-500/15 text-[rgb(var(--lm-text-muted))] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all" data-testid={`del-${item.ref}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-8 text-center text-[rgb(var(--lm-text-muted))] text-sm">Aucune prestation</div>
          )}
        </div>
      )}

      {/* Modale création */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => { setCreating(false); setEditing(null); }}>
          <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))] rounded-xl p-4 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold mb-3 text-[rgb(var(--lm-text))]">Nouvelle prestation</h3>
            <ItemForm item={editing} onChange={setEditing} onSave={() => handleSave(editing)} onCancel={() => { setCreating(false); setEditing(null); }} categories={categories} />
          </div>
        </div>
      )}

      {/* Modale envoi */}
      {sendModal && <SendCatalogueModal categories={categories} onClose={() => setSendModal(false)} />}

      {/* Modale historique des envois */}
      {historyModal && <SentHistoryModal onClose={() => setHistoryModal(false)} />}
    </div>
  );
}

function ItemForm({ item, onChange, onSave, onCancel, categories }) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={item.nom || ''}
          onChange={e => onChange({ ...item, nom: e.target.value })}
          placeholder="Nom"
          className="text-sm h-8 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-[rgb(var(--lm-gold))]"
          data-testid="input-nom"
        />
        <input
          type="text"
          list="cat-list"
          value={item.categorie || ''}
          onChange={e => onChange({ ...item, categorie: e.target.value })}
          placeholder="Catégorie"
          className="text-sm h-8 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-[rgb(var(--lm-gold))]"
          data-testid="input-categorie"
        />
        <datalist id="cat-list">
          {categories.map(c => <option key={c.nom} value={c.nom} />)}
        </datalist>
      </div>
      <input
        type="text"
        value={item.desc || ''}
        onChange={e => onChange({ ...item, desc: e.target.value })}
        placeholder="Description (composition)"
        className="w-full text-sm h-8 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-[rgb(var(--lm-gold))]"
        data-testid="input-desc"
      />
      <div className="flex items-center gap-2">
        <input
          type="number"
          step="0.01"
          value={item.prix_ttc || ''}
          onChange={e => onChange({ ...item, prix_ttc: parseFloat(e.target.value) || 0 })}
          placeholder="Prix"
          className="w-24 text-sm h-8 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-[rgb(var(--lm-gold))] font-mono"
          data-testid="input-prix"
        />
        <input
          type="text"
          value={item.unite || ''}
          onChange={e => onChange({ ...item, unite: e.target.value })}
          placeholder="€"
          className="w-16 text-sm h-8 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-[rgb(var(--lm-gold))]"
          data-testid="input-unite"
        />
        <div className="flex-1" />
        <button onClick={onCancel} className="px-3 h-8 rounded text-[11px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="cancel-edit">
          Annuler
        </button>
        <button onClick={onSave} className="px-3 h-8 rounded bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-[11px] font-bold flex items-center gap-1" data-testid="save-edit">
          <Check size={12} /> Enregistrer
        </button>
      </div>
    </div>
  );
}

function SendCatalogueModal({ categories, onClose }) {
  const [to, setTo] = useState('');
  const [nom, setNom] = useState('');
  const [sujet, setSujet] = useState('Notre catalogue Traiteur — Le Mas');
  const [message, setMessage] = useState('');
  const [contexte, setContexte] = useState('');
  const [ton, setTon] = useState('chaleureux');
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedCats, setSelectedCats] = useState(new Set(categories.map(c => c.nom)));
  const [sending, setSending] = useState(false);

  const toggleCat = (nom) => {
    const s = new Set(selectedCats);
    s.has(nom) ? s.delete(nom) : s.add(nom);
    setSelectedCats(s);
  };

  const toggleAll = () => {
    if (selectedCats.size === categories.length) setSelectedCats(new Set());
    else setSelectedCats(new Set(categories.map(c => c.nom)));
  };

  const handleAiDraft = async () => {
    setAiLoading(true);
    try {
      const r = await fetch(`${API}/api/catalogue-traiteur/ai-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          destinataire_nom: nom,
          destinataire_email: to,
          contexte,
          categories: Array.from(selectedCats),
          ton,
        }),
      });
      const data = await r.json();
      if (r.ok && data.sujet) {
        setSujet(data.sujet);
        setMessage(data.message);
        toast.success('Mail rédigé par l\'IA ✨');
      } else {
        toast.error(data.detail || 'Erreur IA');
      }
    } catch {
      toast.error('Erreur réseau IA');
    }
    setAiLoading(false);
  };

  const handleSend = async () => {
    if (!to || !to.includes('@')) { toast.error('Email invalide'); return; }
    if (selectedCats.size === 0) { toast.error('Sélectionnez au moins une catégorie'); return; }
    setSending(true);
    try {
      const defaultMsg = message || (
        `Bonjour ${nom || ''},\n\n` +
        `Suite à notre échange, je vous transmets en pièce jointe notre catalogue Traiteur du Mas, ` +
        `avec nos prestations sélectionnées pour vous.\n\n` +
        `Je reste à votre disposition pour préparer un devis personnalisé.\n\n` +
        `Bien cordialement,\n\nLaurent SANMARTIN · Le Mas · 04 68 38 06 98`
      );
      const r = await fetch(`${API}/api/catalogue-traiteur/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to, destinataire_nom: nom,
          categories: Array.from(selectedCats),
          sujet, message: defaultMsg,
        }),
      });
      const data = await r.json();
      if (r.ok && data.success) {
        toast.success(`Catalogue envoyé à ${to} (${data.nb_items} prestations)`);
        onClose();
      } else {
        toast.error(data.detail || 'Erreur envoi');
      }
    } catch {
      toast.error('Erreur réseau');
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" onClick={onClose}>
      <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))] rounded-xl p-5 max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Mail size={16} className="text-emerald-400" /> Envoyer le catalogue
          </h3>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase font-bold">Email destinataire *</label>
              <input type="email" value={to} onChange={e => setTo(e.target.value)} placeholder="contact@entreprise.fr"
                className="mt-1 w-full text-sm h-9 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-emerald-400" data-testid="send-to" />
            </div>
            <div>
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase font-bold">Nom (optionnel)</label>
              <input type="text" value={nom} onChange={e => setNom(e.target.value)} placeholder="M. Dupont"
                className="mt-1 w-full text-sm h-9 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-emerald-400" data-testid="send-nom" />
            </div>
          </div>

          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase font-bold">Sujet</label>
            <input type="text" value={sujet} onChange={e => setSujet(e.target.value)}
              className="mt-1 w-full text-sm h-9 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-emerald-400" data-testid="send-sujet" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase font-bold flex items-center gap-1">
                <FolderOpen size={11} /> Catégories à inclure ({selectedCats.size}/{categories.length})
              </label>
              <button onClick={toggleAll} className="text-[10px] text-emerald-400 font-bold hover:underline" data-testid="toggle-all-cats">
                {selectedCats.size === categories.length ? 'Aucune' : 'Toutes'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto p-2 bg-[rgb(var(--lm-bg))] rounded-lg">
              {categories.map(c => (
                <label key={c.nom} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[rgb(var(--lm-card))] cursor-pointer text-[11px] text-[rgb(var(--lm-text))]">
                  <input type="checkbox" checked={selectedCats.has(c.nom)} onChange={() => toggleCat(c.nom)} className="w-3.5 h-3.5" data-testid={`cat-${c.nom}`} />
                  <span className="flex-1 truncate">{c.nom}</span>
                  <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">({c.nb_items})</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase font-bold">Message (optionnel — sinon message par défaut)</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={6}
              placeholder="Bonjour, suite à notre échange..."
              className="mt-1 w-full text-sm p-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-emerald-400 resize-none" data-testid="send-message" />
          </div>

          {/* Bloc IA — pré-remplissage */}
          <div className="rounded-lg border border-purple-400/30 bg-purple-500/5 p-3 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="text-[10px] text-purple-300 uppercase font-bold flex items-center gap-1">
                <Sparkles size={11} /> Rédaction IA — pré-remplir sujet & message
              </label>
              <select value={ton} onChange={e => setTon(e.target.value)}
                className="text-[10px] h-7 px-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none"
                data-testid="ai-ton">
                <option value="chaleureux">Ton chaleureux</option>
                <option value="professionnel">Ton professionnel</option>
                <option value="direct">Ton direct</option>
              </select>
            </div>
            <textarea
              value={contexte}
              onChange={e => setContexte(e.target.value)}
              rows={2}
              placeholder="Ex : Rencontré au salon ProTraiteur, projet mariage juin 2026, 80 pax, recherche menu catalan."
              className="w-full text-sm p-2 rounded bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] focus:outline-none focus:border-purple-400 resize-none"
              data-testid="ai-contexte" />
            <button onClick={handleAiDraft} disabled={aiLoading}
              className="w-full flex items-center justify-center gap-2 px-3 h-8 rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white text-xs font-bold transition-all"
              data-testid="ai-draft-btn">
              {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Rédiger sujet & message avec IA
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-[rgb(var(--lm-border))]/30">
          <a href={`${API}/api/catalogue-traiteur-pdf`} target="_blank" rel="noreferrer"
            className="text-[11px] text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] flex items-center gap-1">
            <FileDown size={12} /> Aperçu PDF complet
          </a>
          <button onClick={handleSend} disabled={sending}
            className="flex items-center gap-2 px-4 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold transition-all"
            data-testid="send-now">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Envoyer le catalogue
          </button>
        </div>
      </div>
    </div>
  );
}


function SentHistoryModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/catalogue-traiteur/sent?limit=100`);
      const data = await r.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Erreur chargement historique');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };

  const totalSent = logs.length;
  const totalOpened = logs.filter(l => (l.open_count || 0) > 0).length;
  const openRate = totalSent ? Math.round(100 * totalOpened / totalSent) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 overflow-y-auto" onClick={onClose}>
      <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))] rounded-xl p-5 max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <History size={16} className="text-emerald-400" /> Envois & Ouvertures
          </h3>
          <button onClick={onClose} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="close-history"><X size={18} /></button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Envoyés</p>
            <p className="text-xl font-mono font-bold text-[rgb(var(--lm-text))]">{totalSent}</p>
          </div>
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Ouverts</p>
            <p className="text-xl font-mono font-bold text-emerald-400">{totalOpened}</p>
          </div>
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 border border-[rgb(var(--lm-border))]/30">
            <p className="text-[9px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold">Taux d'ouverture</p>
            <p className="text-xl font-mono font-bold text-[rgb(var(--lm-gold))]">{openRate}%</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-[rgb(var(--lm-text-muted))] text-sm">Aucun envoi pour l'instant</div>
        ) : (
          <div className="bg-[rgb(var(--lm-bg))] rounded-lg border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20 max-h-[55vh] overflow-y-auto">
            {logs.map((l, i) => {
              const opened = (l.open_count || 0) > 0;
              return (
                <div key={l.id || i} className="px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-[rgb(var(--lm-card))]/40" data-testid={`log-${i}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {opened ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold flex items-center gap-1">
                          <Eye size={10} /> Ouvert ×{l.open_count}
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[rgb(var(--lm-border))]/30 text-[rgb(var(--lm-text-muted))] font-bold">Non ouvert</span>
                      )}
                      <p className="text-sm font-bold text-[rgb(var(--lm-text))] truncate">{l.to}</p>
                    </div>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-0.5 truncate">
                      {l.subject} · {l.nb_items || 0} prestations
                      {l.categories?.length ? ` · ${l.categories.length} cat.` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Envoyé</p>
                    <p className="text-[11px] font-mono text-[rgb(var(--lm-text))]">{fmtDate(l.sent_at)}</p>
                    {opened && (
                      <p className="text-[10px] font-mono text-emerald-400 mt-0.5">
                        1er: {fmtDate(l.first_opened_at)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between">
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">
            Le tracking pixel détecte l'ouverture du mail HTML (certains clients bloquent les images).
          </p>
          <button onClick={load} className="text-[11px] text-emerald-400 font-bold hover:underline" data-testid="refresh-history">Rafraîchir</button>
        </div>
      </div>
    </div>
  );
}
